/**
 * 🔒 AdminCoachingContext — Audit RGPD des partages Owen → Coach humain
 *
 * Lecture seule. Liste paginée, filtres de statut, détail avec synthèse + audit trail.
 * Aucune possibilité de modification : l'admin ne peut pas créer/révoquer un partage
 * (réservé au coaché).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Shield, RefreshCw, CheckCircle2, Clock, XCircle,
  ChevronRight, FileLock2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const T = {
  bg: '#FBF9F3', card: '#FFFFFF', ink: '#1C1147', text: '#1F2937',
  muted: '#6B7280', border: '#E8E2D2', gold: '#D4AF37', goldDeep: '#B7901F',
  ivoryDeep: '#FFFCEC', green: '#48825A', red: '#9B2C1F',
};

const STATUS_META = {
  active:  { label: 'Actif',   color: T.green, icon: CheckCircle2 },
  expired: { label: 'Expiré',  color: T.muted, icon: Clock },
  revoked: { label: 'Révoqué', color: T.red,   icon: XCircle },
};

const SCOPE_LABEL = {
  A: 'Synthèse courte',
  B: 'Synthèse + objectifs',
  C: 'Synthèse + rapports IA',
  D: 'Personnalisé',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
};

const PAGE = 50;

function StatusFilter({ value, onChange }) {
  const opts = [
    { v: '',        label: 'Tous' },
    { v: 'active',  label: 'Actifs' },
    { v: 'expired', label: 'Expirés' },
    { v: 'revoked', label: 'Révoqués' },
  ];
  return (
    <div className="flex gap-2 flex-wrap" data-testid="admin-ctx-status-filters">
      {opts.map((o) => (
        <button
          key={o.v || 'all'}
          onClick={() => onChange(o.v)}
          data-testid={`admin-ctx-filter-${o.v || 'all'}`}
          className="px-3 py-1.5 rounded-full text-xs font-semibold transition"
          style={{
            background: value === o.v ? T.gold : T.card,
            color: value === o.v ? T.ink : T.muted,
            border: `1px solid ${value === o.v ? T.goldDeep : T.border}`,
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function DetailModal({ shareId, token, API, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await fetch(`${API}/admin/coaching-context-shares/${shareId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
      } catch (e) {
        toast.error(`Erreur : ${e.message}`);
        onClose();
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [API, shareId, token, onClose]);

  const share = data?.share;
  const summary = data?.summary;
  const s = summary?.synthesis || {};
  const audit = share?.audit_trail || [];

  return (
    <div
      data-testid="admin-ctx-detail-modal"
      className="fixed inset-0 z-[10000] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(12,8,32,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-3xl w-full my-4 rounded-2xl overflow-hidden"
        style={{ background: T.bg, border: `1px solid ${T.border}`, boxShadow: '0 24px 60px -12px rgba(28,17,71,0.45)' }}
      >
        <div
          className="px-6 py-4 flex justify-between items-start"
          style={{ background: `linear-gradient(135deg, ${T.ink} 0%, #3A2E6E 100%)`, color: '#FFF8E0' }}
        >
          <div>
            <div className="flex items-center gap-2">
              <FileLock2 className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-[0.24em] font-semibold">
                Audit RGPD · Partage Owen
              </span>
            </div>
            <div className="text-xs mt-1 font-mono" style={{ color: '#F3D060' }}>
              share_id: {shareId}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-ctx-detail-close"
            className="text-white/70 hover:text-white p-1"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading && (
            <div className="py-10 text-center text-sm" style={{ color: T.muted }}>
              Chargement…
            </div>
          )}
          {!loading && data && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><b>Utilisateur :</b> <span className="font-mono text-xs">{share?.user_id}</span></div>
                <div><b>Coach :</b> <span className="font-mono text-xs">{share?.coach_user_id}</span></div>
                <div><b>Booking :</b> <span className="font-mono text-xs">{share?.booking_id}</span></div>
                <div><b>Scope :</b> {share?.consent_scope} · {SCOPE_LABEL[share?.consent_scope]}</div>
                <div><b>Statut :</b> {STATUS_META[share?.status]?.label || share?.status}</div>
                <div><b>Expire :</b> {fmtDate(share?.expires_at)}</div>
                <div><b>Consenti :</b> {fmtDate(share?.consented_at)}</div>
                {share?.revoked_at && <div><b>Révoqué :</b> {fmtDate(share?.revoked_at)}</div>}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: T.goldDeep }}>
                  Catégories autorisées
                </div>
                <div className="flex flex-wrap gap-1">
                  {(share?.allowed_categories || []).map((c) => (
                    <span key={c} className="px-2 py-0.5 rounded text-[10px]"
                      style={{ background: T.ivoryDeep, color: T.text, border: `1px solid ${T.border}` }}>
                      {c}
                    </span>
                  ))}
                </div>
              </div>

              {summary && (
                <div data-testid="admin-ctx-detail-synthesis"
                  className="p-4 rounded-xl space-y-2 text-sm"
                  style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text }}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: T.goldDeep }}>
                    Synthèse IA (identique à celle vue par l'utilisateur et le coach)
                  </div>
                  {s.contexte_parcours && <div><b>Contexte :</b> {s.contexte_parcours}</div>}
                  {s.action_progres && <div><b>Action :</b> {s.action_progres}</div>}
                  {s.recommandation_coach && <div><b>Reco :</b> {s.recommandation_coach}</div>}
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: T.goldDeep }}>
                  Audit trail ({audit.length} événement{audit.length > 1 ? 's' : ''})
                </div>
                <div
                  className="rounded-xl p-3 text-xs font-mono max-h-60 overflow-y-auto"
                  style={{ background: T.ivoryDeep, border: `1px solid ${T.border}`, color: T.text }}
                  data-testid="admin-ctx-detail-audit"
                >
                  {audit.length === 0 && <div style={{ color: T.muted }}>Aucun événement.</div>}
                  {audit.map((e, i) => (
                    <div key={i} className="py-0.5">
                      <span style={{ color: T.goldDeep }}>{fmtDate(e.at)}</span> · {e.event}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminCoachingContext() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const API = getApiUrl();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [openShareId, setOpenShareId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/admin/coaching-context-shares`);
      if (status) url.searchParams.set('status', status);
      url.searchParams.set('offset', offset);
      url.searchParams.set('limit', PAGE);
      const r = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
      setTotal(j.total || 0);
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [API, offset, status, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const kpis = {
    total,
    active: items.filter((s) => s.status === 'active').length,
    expired: items.filter((s) => s.status === 'expired').length,
    revoked: items.filter((s) => s.status === 'revoked').length,
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate('/training')}
          data-testid="admin-ctx-back-btn"
          className="flex items-center gap-2 text-sm mb-4 text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Retour modules admin
        </button>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="uppercase text-[11px] tracking-[0.24em] font-semibold" style={{ color: T.gold }}>
              RGPD · Partage Owen → Coach humain
            </p>
            <h1 className="text-3xl mt-1" style={{ color: '#FFF', fontFamily: 'Georgia, serif', fontWeight: 600 }}>
              Audit du contexte partagé
            </h1>
            <p className="text-sm mt-1 text-zinc-400">
              Lecture seule · Consentements explicites · Révocations et expirations tracées
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="admin-ctx-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            style={{ background: T.gold, color: T.ink }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total partages', value: kpis.total, testId: 'admin-ctx-kpi-total' },
            { label: 'Actifs (page)',  value: kpis.active, testId: 'admin-ctx-kpi-active' },
            { label: 'Expirés (page)', value: kpis.expired, testId: 'admin-ctx-kpi-expired' },
            { label: 'Révoqués (page)', value: kpis.revoked, testId: 'admin-ctx-kpi-revoked' },
          ].map((k) => (
            <div
              key={k.label}
              data-testid={k.testId}
              className="rounded-xl p-4"
              style={{ background: T.bg, border: `1px solid ${T.border}` }}
            >
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: T.goldDeep }}>
                {k.label}
              </div>
              <div className="text-2xl mt-1" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 600 }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <StatusFilter value={status} onChange={(v) => { setStatus(v); setOffset(0); }} />
        </div>

        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: T.bg, border: `1px solid ${T.border}` }}
        >
          {loading && (
            <div className="p-10 text-center text-sm" style={{ color: T.muted }}>
              Chargement…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div data-testid="admin-ctx-empty" className="p-10 text-center">
              <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: T.goldDeep }} />
              <p className="text-sm" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 600 }}>
                Aucun partage pour ce filtre.
              </p>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div data-testid="admin-ctx-list" className="divide-y" style={{ borderColor: T.border }}>
              {items.map((s) => {
                const meta = STATUS_META[s.status] || STATUS_META.active;
                const Icon = meta.icon;
                return (
                  <motion.button
                    key={s.share_id}
                    type="button"
                    onClick={() => setOpenShareId(s.share_id)}
                    data-testid={`admin-ctx-row-${s.share_id}`}
                    className="w-full text-left p-4 hover:bg-white transition flex items-center justify-between gap-3"
                    whileHover={{ x: 2 }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Icon className="w-4 h-4" style={{ color: meta.color }} />
                        <span className="text-xs font-semibold" style={{ color: meta.color }}>
                          {meta.label}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold"
                          style={{ background: T.ivoryDeep, color: T.goldDeep, border: `1px solid ${T.border}` }}>
                          Scope {s.consent_scope}
                        </span>
                      </div>
                      <div className="text-xs font-mono truncate" style={{ color: T.ink }}>
                        user: {s.user_id} · coach: {s.coach_user_id}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: T.muted }}>
                        Consenti {fmtDate(s.consented_at)} · Expire {fmtDate(s.expires_at)}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: T.muted }} />
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > PAGE && (
          <div className="flex justify-between items-center mt-4 text-xs" style={{ color: '#D4D4D8' }}>
            <div>
              {offset + 1}–{Math.min(offset + PAGE, total)} sur {total}
            </div>
            <div className="flex gap-2">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
                data-testid="admin-ctx-prev"
                className="px-3 py-1 rounded-full disabled:opacity-40"
                style={{ background: T.bg, color: T.ink, border: `1px solid ${T.border}` }}
              >
                Précédent
              </button>
              <button
                disabled={offset + PAGE >= total}
                onClick={() => setOffset(offset + PAGE)}
                data-testid="admin-ctx-next"
                className="px-3 py-1 rounded-full disabled:opacity-40"
                style={{ background: T.gold, color: T.ink }}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {openShareId && (
        <DetailModal
          shareId={openShareId}
          token={token}
          API={API}
          onClose={() => { setOpenShareId(null); fetchData(); }}
        />
      )}
    </div>
  );
}
